/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useState } from 'react';
import { View, Text, Icon, Actionable } from '@az/starc-ui';
import { ChevronRight } from '@az/starc-ui-icons';
import { CMSLink } from '@/features/contentstack/components/CMSLink/CMSLink';
import styles from './ExpandableSecondaryLink.module.scss';
import type { ContentStackLink } from '@/features/contentstack/interface/content-types';

type MenuNavigationItem = {
  menu_heading?: string;
  menu_heading_link?: string;
  section_title?: string;
  section_cta?: ContentStackLink;
  menu_link_group?: Array<{
    menu_link?: ContentStackLink;
  }>;
  feature_link_content?: {
    image_url?: string;
    heading?: string;
    sub_heading?: string;
    cta?: ContentStackLink;
  };
};

type ExpandableSecondaryLinkProps = {
  section: MenuNavigationItem;
  className?: string;
  handleCloseModal: () => void;
};

export const ExpandableSecondaryLink = ({
  section,
  className,
  handleCloseModal,
}: ExpandableSecondaryLinkProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = !!section.menu_link_group && section.menu_link_group.length > 0;

  const handleClick = () => {
    if (hasChildren) {
      setIsExpanded((expanded) => !expanded);
    }
  };

  return (
    <View direction="column" className={styles.expandableSecondaryLink}>
      <Actionable
        onClick={hasChildren ? handleClick : handleCloseModal}
        href={!hasChildren ? section.menu_heading_link || '#' : undefined}
        className={className}
        aria-expanded={hasChildren ? isExpanded : undefined}
      >
        <Text weight="bold" className={styles.secondaryLinksText}>
          {section.menu_heading}
        </Text>
        <View className={`${styles.chevronIcon} ${isExpanded ? styles.expanded : ''}`}>
          <Icon size={4.5} svg={ChevronRight} />
        </View>
      </Actionable>
      {hasChildren && isExpanded && (
        <View direction="column" className={styles.childLinksContainer}>
          {section.menu_link_group?.map((linkItem) => {
            if (!linkItem.menu_link?.href) {
              return null;
            }

            return (
              <CMSLink
                key={linkItem.menu_link?.title}
                to={linkItem.menu_link?.href}
                className={styles.childLink}
                onClick={() => {
                  handleCloseModal();
                }}
              >
                <Text weight="regular" size="087" className={styles.childLinkText}>
                  {linkItem.menu_link?.title}
                </Text>
              </CMSLink>
            );
          })}
        </View>
      )}
    </View>
  );
};
